import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Center,
  VStack,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Button,
  HStack,
  Divider,
  Text,
  useToast,
} from "@chakra-ui/react";
import Navbar from "../../components/Navbar";
import useAxios from "../../utils/useAxios";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import SmallMapWidget from "../SmallMapWidget";

interface ILocation {
  latitude: number;
  longitude: number;
  altitude?: number | null;
  accuracy: number;
  altitudeAccuracy?: number | null;
  heading?: number | null;
  speed?: number | null;
}

const ManualOverview: React.FC = () => {
  const { label_code } = useParams<{ label_code: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const api = useAxios();

  const deviceAddress = label_code ?? "";

  const [deviceType, setDeviceType] = useState<string>("");
  const [deviceName, setDeviceName] = useState<string>("");
  const [sensorDepth, setSensorDepth] = useState<number>();
  const [location, setLocation] = useState<ILocation>();
  const [manualLat, setManualLat] = useState<string>("");
  const [manualLng, setManualLng] = useState<string>("");
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);

  useEffect(() => {
    if (!deviceAddress) return;

    api
      .post("/frontend/devices/type/", { label_code: deviceAddress })
      .then((res) => {
        setDeviceType(res.data.device_type.toUpperCase());
      })
      .catch(() => {
        toast({
          title: <Trans>Unable to detect device type</Trans>,
          status: "error",
        });
      });
  }, [deviceAddress, api, toast]);

  const getLocation = () => {
    if (!navigator.geolocation) {
      toast({
        title: <Trans>Geolocation not supported</Trans>,
        status: "error",
      });
      return;
    }
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          altitude: pos.coords.altitude,
          accuracy: pos.coords.accuracy,
          altitudeAccuracy: pos.coords.altitudeAccuracy,
          heading: pos.coords.heading,
          speed: pos.coords.speed,
        });
        setLocationLoading(false);
      },
      () => {
        toast({
          title: <Trans>Unable to retrieve your location</Trans>,
          status: "error",
        });
        setLocationLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  };

  const handleSubmit = async () => {
    let lat = location?.latitude;
    let lng = location?.longitude;
    let accuracy = location?.accuracy;

    if (showManualEntry) {
      if (!manualLat || !manualLng) {
        toast({
          title: <Trans>Enter both latitude and longitude</Trans>,
          status: "error",
        });
        return;
      }
      lat = parseFloat(manualLat);
      lng = parseFloat(manualLng);
      accuracy = 0;
    }

    try {
      const payload: any = {
        label_code: deviceAddress,
        device_name: deviceName.trim(),
        latitude: lat,
        longitude: lng,
        accuracy: accuracy,
        altitude: location?.altitude,
        altitude_accuracy: location?.altitudeAccuracy,
      };

      if (deviceType === "SENSOR") {
        payload.sensor_depth = sensorDepth;
      }

      const res = await api.post("/frontend/devices/custom-associate/", payload);

      toast({
        title:
          res.status === 201 ? (
            <Trans>Device associated successfully</Trans>
          ) : (
            <Trans>Device updated successfully</Trans>
          ),
        status: "success",
      });
      navigate("/devices");
    } catch (err: any) {
      toast({
        title: err.response?.data?.error || <Trans>Unknown error</Trans>,
        status: "error",
      });
    }
  };

  return (
    <Navbar>
      <Center py={8}>
        <Box p={6} bg="white" boxShadow="lg" borderRadius="md" width={{ base: "90%", md: 500 }}>
          <VStack spacing={4} align="stretch">
            <Heading size="lg" textAlign="center">
              <Trans>Configure Device</Trans>
            </Heading>

            <Text>
              <Trans>Device Code:</Trans> <strong>{deviceAddress}</strong>
            </Text>

            <FormControl>
              <FormLabel>
                <Trans>Name (optional)</Trans>
              </FormLabel>
              <Input
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                placeholder={t`My Device`}
              />
            </FormControl>

            {deviceType === "SENSOR" && (
              <FormControl>
                <FormLabel>
                  <Trans>Sensor Depth (cm)</Trans>
                </FormLabel>
                <Input
                  type="number"
                  value={sensorDepth ?? ""}
                  onChange={(e) => setSensorDepth(+e.target.value)}
                  placeholder={t`e.g. 15`}
                />
              </FormControl>
            )}

            <Divider />

            <HStack spacing={4} align="center">
              <Button
                onClick={getLocation}
                variant="outline"
                isLoading={locationLoading}
                disabled={showManualEntry}
                colorScheme="teal"
              >
                <Trans>Register location</Trans>
              </Button>
              <Divider orientation="vertical" height="24px" />
              <Button onClick={() => setIsMapOpen(true)} variant="outline" colorScheme="teal">
                <Trans>Pick on map</Trans>
              </Button>
            </HStack>

            <Text textAlign="left">
              <Trans>or</Trans>
            </Text>

            <Button
              onClick={() => setShowManualEntry(!showManualEntry)}
              variant="outline"
              colorScheme="teal"
            >
              <Trans>Enter coordinates manually</Trans>
            </Button>

            {showManualEntry && (
              <VStack align="stretch">
                <FormControl>
                  <FormLabel>
                    <Trans>Latitude</Trans>
                  </FormLabel>
                  <Input
                    value={manualLat}
                    onChange={(e) => setManualLat(e.target.value)}
                    placeholder={t`e.g. 51.5074`}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>
                    <Trans>Longitude</Trans>
                  </FormLabel>
                  <Input
                    value={manualLng}
                    onChange={(e) => setManualLng(e.target.value)}
                    placeholder={t`e.g. -0.1278`}
                  />
                </FormControl>
              </VStack>
            )}

            {location && !showManualEntry ? (
              <HStack spacing={3}>
                <Text>
                  <Trans>Latitude:</Trans> {location.latitude}
                </Text>
                <Text>
                  <Trans>Longitude:</Trans> {location.longitude}
                </Text>
                <Text>
                  <Trans>Accuracy:</Trans> {location.accuracy.toFixed(0)}m
                </Text>
              </HStack>
            ) : null}

            <Button mt={4} colorScheme="teal" onClick={handleSubmit}>
              <Trans>Save Device</Trans>
            </Button>
          </VStack>
        </Box>
      </Center>

      <SmallMapWidget
        isOpen={isMapOpen}
        onClose={() => setIsMapOpen(false)}
        onSelect={(lat, lng) => {
          setLocation({
            latitude: lat,
            longitude: lng,
            accuracy: 0,
            altitude: null,
            altitudeAccuracy: null,
            heading: null,
            speed: null,
          });
          setIsMapOpen(false);
        }}
      />
    </Navbar>
  );
};

export default ManualOverview;
